import React, { useState } from 'react';
import { X, CheckCircle2, AlertCircle, ArrowRight, ShieldCheck, Scale, Users, FileCheck } from 'lucide-react';

interface TaxDiagnosticIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
  originTab?: 'quick' | 'reforma' | 'audit';
}

const REVENUE_TIERS = [
  { id: 'small', label: 'Até R$ 360 mil/ano', subtitle: 'Pequeno Porte / MEI / EPP' },
  { id: 'medium', label: 'R$ 360k a R$ 3,6M/ano', subtitle: 'Médio Porte / Simples / Presumido' },
  { id: 'large', label: 'Acima de R$ 3,6M/ano', subtitle: 'Grande Porte / Lucro Real' },
];

export const TaxDiagnosticIntroModal: React.FC<TaxDiagnosticIntroModalProps> = ({
  isOpen,
  onClose,
  originTab = 'reforma',
}) => {
  const [revenueTier, setRevenueTier] = useState<'small' | 'medium' | 'large'>('medium');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [acceptedLgpd, setAcceptedLgpd] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const formatPhone = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  };

  const selectedTierInfo = REVENUE_TIERS.find((t) => t.id === revenueTier);

  const buildWhatsappFallbackUrl = () => {
    const text = encodeURIComponent(
      `Olá! Tenho interesse no Diagnóstico Tributário Productizado (R-MAX + 4Shift Gestão).\n\n` +
      `*Nome:* ${name || 'Não informado'}\n` +
      `*WhatsApp:* ${phone}\n` +
      `*Porte / Faturamento:* ${selectedTierInfo?.label}\n` +
      `*Origem:* Calculadora R-MAX (${originTab})`
    );
    return `https://api.whatsapp.com/send/?phone=5511972269833&text=${text}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const rawDigits = phone.replace(/\D/g, '');
    if (rawDigits.length < 10) {
      setErrorMessage('Por favor, informe um WhatsApp válido com DDD.');
      return;
    }

    if (!acceptedLgpd) {
      setErrorMessage('É necessário concordar com a política de privacidade e LGPD.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/leads/calculator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_source: 'tax_diagnostic_pilot',
          name,
          phone,
          annual_revenue_tier: revenueTier,
          origin_tab: originTab,
          metadata: {
            tierLabel: selectedTierInfo?.label,
            submittedAt: new Date().toISOString(),
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Falha na comunicação com o servidor.');
      }

      setIsSuccess(true);
    } catch (err: any) {
      console.warn('Falha no envio direto do lead do diagnóstico, ativando fallback WhatsApp:', err);
      setErrorMessage(
        'Não foi possível registrar automaticamente pelo navegador. Clique no botão abaixo para concluir sua reserva diretamente no WhatsApp Comercial da 4Shift / R-MAX.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden text-slate-800">
        
        {/* Cabeçalho */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/80">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-800 flex items-center justify-center font-black text-sm">
              <Scale className="w-4 h-4 text-teal-700" />
            </div>
            <div>
              <h3 className="text-base font-black text-slate-900">
                Diagnóstico de Regime Tributário
              </h3>
              <p className="text-xs text-slate-500">
                Parceria técnica R-MAX + 4Shift Gestão Contábil
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          {isSuccess ? (
            <div className="text-center py-4 space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-teal-50 text-teal-600 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <div>
                <h4 className="text-lg font-black text-slate-900">
                  Interesse Registrado no Piloto
                </h4>
                <p className="text-xs text-slate-600 mt-1 max-w-sm mx-auto">
                  Recebemos sua solicitação para a faixa de <strong>{selectedTierInfo?.label}</strong>. A equipe de especialistas fiscais da <strong>4Shift Gestão</strong> entrará em contato para agendar sua análise e devolutiva técnica.
                </p>
              </div>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-left text-xs space-y-2 text-slate-700">
                <div className="flex items-center gap-1.5 font-bold text-slate-900">
                  <ShieldCheck className="w-4 h-4 text-teal-600" />
                  <span>Diferencial de Mercado</span>
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Ao contrário de simuladores engessados de ERP (como Senior Sistemas a R$ 1.516/mês com contrato de 1 ano), nosso diagnóstico é por pagamento único, sem fidelidade e com validação contábil humana real.
                </p>
              </div>

              <div className="pt-2">
                <button
                  onClick={onClose}
                  className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs transition-colors"
                >
                  Concluir e Voltar à Calculadora
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              
              {/* Box Comparativo / Valor */}
              <div className="p-3 bg-teal-50/60 border border-teal-200/80 rounded-xl text-xs text-teal-950 space-y-1.5">
                <div className="flex items-center justify-between font-bold">
                  <div className="flex items-center gap-1.5">
                    <FileCheck className="w-4 h-4 text-teal-700" />
                    <span>Análise dos 4 Regimes + Reforma Tributária</span>
                  </div>
                  <a
                    href="https://resolvmax.com.br/diagnostico-tributario"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-teal-800 hover:underline flex items-center gap-0.5"
                  >
                    <span>Ver página oficial</span>
                    <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
                <p className="text-[11px] text-teal-900 leading-relaxed">
                  Comparação técnica com os números reais da sua empresa entre MEI, Simples Nacional, Lucro Presumido e Lucro Real, validada por contador especialista.
                </p>
              </div>

              {/* Qualificação 1: Faturamento Anual (Chips M3) */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  1. Faturamento Anual Estimado da Empresa *
                </label>
                <div className="space-y-2">
                  {REVENUE_TIERS.map((tier) => {
                    const isSelected = revenueTier === tier.id;
                    return (
                      <button
                        key={tier.id}
                        type="button"
                        onClick={() => setRevenueTier(tier.id as any)}
                        className={`w-full py-2.5 px-3 text-left rounded-xl text-xs border transition-all flex items-center justify-between ${
                          isSelected
                            ? 'bg-teal-700 text-white border-teal-700 shadow-xs'
                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <div>
                          <div className="font-bold text-xs">{tier.label}</div>
                          <div className={`text-[10px] ${isSelected ? 'text-teal-100' : 'text-slate-400'}`}>
                            {tier.subtitle}
                          </div>
                        </div>
                        <div
                          className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                            isSelected ? 'border-white bg-white text-teal-700' : 'border-slate-300'
                          }`}
                        >
                          {isSelected && <div className="w-2 h-2 rounded-full bg-teal-700" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Qualificação 2: Nome */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  2. Nome do Responsável / Sócio *
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Mariana Castro"
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              {/* Qualificação 3: WhatsApp */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  3. WhatsApp para Contato e Devolutiva *
                </label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="(11) 99999-9999"
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              {/* Checkbox LGPD */}
              <div className="pt-1">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedLgpd}
                    onChange={(e) => setAcceptedLgpd(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                  />
                  <span className="text-[11px] text-slate-500 leading-tight">
                    Concordo em compartilhar esses dados para avaliação preliminar de enquadramento tributário com os especialistas da 4Shift Gestão.
                  </span>
                </label>
              </div>

              {/* Erro ou Fallback Ativo */}
              {errorMessage && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 space-y-2">
                  <div className="flex items-start gap-1.5">
                    <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                    <span>{errorMessage}</span>
                  </div>
                  <a
                    href={buildWhatsappFallbackUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 w-full py-2 px-3 bg-teal-700 hover:bg-teal-600 text-white font-bold rounded-lg text-xs transition-colors"
                  >
                    <span>Concluir Reserva pelo WhatsApp</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}

              {/* Botão de Envio */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-teal-700 hover:bg-teal-600 disabled:bg-slate-300 text-white font-bold rounded-xl text-xs shadow-md shadow-teal-700/20 transition-all hover:scale-[1.01] active:scale-[0.99]"
                >
                  {isLoading ? (
                    <span>Registrando interesse...</span>
                  ) : (
                    <>
                      <span>Solicitar Diagnóstico com Contador</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

      </div>
    </div>
  );
};
